/** Data access for work orders. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import {
  compactParams,
  type ListState,
  type Paged,
  type PageOf,
  usePagedQuery,
  usePageQuery,
} from "@/shared/lib/paging";

/** Work order list row. */
export type WorkOrder = Schemas["WorkOrderOut"];
/** Work order with execution records. */
export type WorkOrderDetail = Schemas["WorkOrderDetailOut"];
/** Create payload. */
export type WorkOrderIn = Schemas["WorkOrderIn"];
/** Shared planning fields used by create, edit, plan and schedule forms. */
export type WorkOrderPlanningFields = Pick<
  Schemas["WorkOrderPatch"],
  | "assigned_team_id"
  | "vendor_id"
  | "operating_unit_id"
  | "cost_center_id"
  | "due_at"
  | "scheduled_start"
  | "scheduled_end"
  | "est_hours"
  | "restriction_scope"
>;
/** Atomic work-order planning command. */
export type WorkOrderPlanningCommand = Schemas["WorkOrderPlanningCommandIn"];
/** Transition payload. */
export type TransitionIn = Schemas["TransitionIn"];
/** Transition option. */
export type TransitionOption = Schemas["TransitionOptionOut"];
/** Labour entry payload. */
export type LabourIn = Schemas["LabourIn"];
/** Task row. */
export type Task = Schemas["TaskOut"];
/** Editable task definition, separate from execution status. */
export type TaskDefinitionIn = Schemas["TaskDefinitionIn"];
/** Checklist row. */
export type ChecklistItem = Schemas["ChecklistOut"];
/** Labour row. */
export type Labour = Schemas["LabourOut"];
/** Part usage row. */
export type Part = Schemas["PartOut"];
/** Add-part payload. */
export type PartIn = Schemas["PartIn"];
/** Issue/return payload. */
export type PartIssueIn = Schemas["PartIssueIn"];
/** Failure report payload. */
export type FailureReportIn = Schemas["FailureReportIn"];
/** Failure report. */
export type FailureReport = Schemas["FailureReportOut"];
/** Meter reading payload. */
export type ReadingIn = Schemas["ReadingIn"];
/** A tenant member who may be assigned to a work order. */
export type WorkOrderAssigneeOption = Schemas["MemberOut"];

/** Work order list filters (mirror the API query params). */
export interface WorkOrderFilters {
  q?: string;
  status?: string;
  open_only?: boolean;
  type?: string;
  priority?: string;
  asset_id?: string;
  mine?: boolean;
  site_id?: string;
  overdue?: boolean;
  due_after?: string;
  due_before?: string;
}

/** Work order enumerations. */
export const WO_TYPES = [
  "preventive",
  "corrective",
  "inspection",
  "modification",
  "project",
] as const;
export const WO_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export const WO_STATUSES = [
  "draft",
  "planned",
  "scheduled",
  "in_progress",
  "on_hold",
  "completed",
  "verified",
  "closed",
  "cancelled",
] as const;

/** Invalidate everything that a work-order change may affect. */
function invalidateWorkOrders(qc: ReturnType<typeof useQueryClient>, id?: string): void {
  void qc.invalidateQueries({ queryKey: ["work-orders"] });
  void qc.invalidateQueries({ queryKey: ["dashboard"] });
  void qc.invalidateQueries({ queryKey: ["schedules"] });
  if (id) {
    void qc.invalidateQueries({ queryKey: ["work-order", id] });
  }
}

/** Paged work order list. */
export function useWorkOrders(filters: WorkOrderFilters): Paged<WorkOrder> {
  return usePagedQuery(["work-orders", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/work-orders/", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Sorted, numbered page of work orders for the list table. */
export function useWorkOrderPage(filters: WorkOrderFilters, state: ListState): PageOf<WorkOrder> {
  return usePageQuery(["work-orders", "page", filters], state, async (params) =>
    unwrap(
      await api.GET("/api/v1/work-orders/", {
        params: { query: compactParams({ ...filters, ...params }) },
      }),
    ),
  );
}

/** Single work order with details. */
export function useWorkOrder(id: string): UseQueryResult<WorkOrderDetail> {
  return useQuery({
    queryKey: ["work-order", id],
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/work-orders/{wo_id}", { params: { path: { wo_id: id } } })),
  });
}

/** Asset allocation inherited by newly created work orders. */
export function useWorkOrderAssetDefaults(assetId: string): UseQueryResult<Schemas["AssetOut"]> {
  return useQuery({
    queryKey: ["work-order-asset-defaults", assetId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/{asset_id}", {
          params: { path: { asset_id: assetId } },
        }),
      ),
    enabled: Boolean(assetId),
  });
}

/** Active tenant members available for work-order assignment. */
export function useWorkOrderAssigneeOptions(
  enabled: boolean,
  search?: string,
): Paged<WorkOrderAssigneeOption> {
  return usePagedQuery(
    ["work-order-assignee-options", search],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/identity/members", {
          params: { query: compactParams({ status: "active", q: search, cursor, limit: 50 }) },
        }),
      ),
    { enabled },
  );
}

/** Replace a work order's assignees. */
export function useSetWorkOrderAssignees(
  id: string,
): UseMutationResult<WorkOrderDetail, Error, { membership_ids: string[] }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/planning", {
          params: { path: { wo_id: id } },
          body: { action: "save", assignee_ids: body.membership_ids },
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Update work-order planning fields. */
export function useUpdateWorkOrder(
  id: string,
): UseMutationResult<WorkOrderDetail, Error, Schemas["WorkOrderPatch"]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.PATCH("/api/v1/work-orders/{wo_id}", {
          params: { path: { wo_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Atomically save planning fields with optional plan/schedule transition and assignments. */
export function useApplyPlanningCommand(
  id: string,
): UseMutationResult<WorkOrderDetail, Error, WorkOrderPlanningCommand> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/planning", {
          params: { path: { wo_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Create a work order. */
export function useCreateWorkOrder(): UseMutationResult<WorkOrderDetail, Error, WorkOrderIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/work-orders/", { body })),
    onSuccess: () => invalidateWorkOrders(qc),
  });
}

/** Apply a workflow transition. */
export function useTransition(
  id: string,
): UseMutationResult<WorkOrderDetail, Error, { name: string; body: TransitionIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, body }) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/transitions/{name}", {
          params: { path: { wo_id: id, name } },
          body,
        }),
      ),
    onSuccess: () => {
      invalidateWorkOrders(qc, id);
      void qc.invalidateQueries({ queryKey: ["assets"] });
      void qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

/** Save task planning fields independently from execution. */
export function useSaveTaskDefinition(
  id: string,
): UseMutationResult<Task, Error, { taskId?: string; body: TaskDefinitionIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, body }) =>
      taskId
        ? unwrap(
            await api.PUT("/api/v1/work-orders/{wo_id}/tasks/{task_id}", {
              params: { path: { wo_id: id, task_id: taskId } },
              body,
            }),
          )
        : unwrap(
            await api.POST("/api/v1/work-orders/{wo_id}/tasks", {
              params: { path: { wo_id: id } },
              body,
            }),
          ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Update a task's execution status. */
export function useUpdateTask(
  id: string,
): UseMutationResult<Task, Error, { taskId: string; status: string; notes?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, status, notes = "" }) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/tasks/{task_id}", {
          params: { path: { wo_id: id, task_id: taskId } },
          body: { status, notes },
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Answer a checklist item. */
export function useAnswerChecklist(
  id: string,
): UseMutationResult<
  ChecklistItem,
  Error,
  { itemId: string; value: unknown; note?: string; photoDocumentId?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, value, note = "", photoDocumentId = null }) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/checklist/{item_id}", {
          params: { path: { wo_id: id, item_id: itemId } },
          body: { value, note, photo_document_id: photoDocumentId },
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Record labour. */
export function useAddLabour(id: string): UseMutationResult<Labour, Error, LabourIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/labour", {
          params: { path: { wo_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Plan a part on the work order. */
export function useAddPart(id: string): UseMutationResult<Part, Error, PartIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/parts", {
          params: { path: { wo_id: id } },
          body,
        }),
      ),
    onSuccess: () => {
      invalidateWorkOrders(qc, id);
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

/** Issue or return a planned part against stock. */
export function usePartMovement(
  id: string,
): UseMutationResult<
  Part,
  Error,
  { partId: string; action: "issue" | "return"; body: PartIssueIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partId, action, body }) =>
      action === "issue"
        ? unwrap(
            await api.POST("/api/v1/work-orders/{wo_id}/parts/{part_id}/issue", {
              params: { path: { wo_id: id, part_id: partId } },
              body,
            }),
          )
        : unwrap(
            await api.POST("/api/v1/work-orders/{wo_id}/parts/{part_id}/return", {
              params: { path: { wo_id: id, part_id: partId } },
              body,
            }),
          ),
    onSuccess: () => {
      invalidateWorkOrders(qc, id);
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

/** Set the failure report. */
export function useSetFailureReport(
  id: string,
): UseMutationResult<FailureReport, Error, FailureReportIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.PUT("/api/v1/work-orders/{wo_id}/failure-report", {
          params: { path: { wo_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateWorkOrders(qc, id),
  });
}

/** Record a meter reading (linked to the work order). */
export function useRecordReading(): UseMutationResult<
  unknown,
  Error,
  { meterId: string; body: ReadingIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ meterId, body }) =>
      unwrap(
        await api.POST("/api/v1/meters/{meter_id}/readings", {
          params: { path: { meter_id: meterId } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assets"] });
      void qc.invalidateQueries({ queryKey: ["schedules"] });
    },
  });
}
